import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { getApiErrorMessage, inr, todayISO, formatDate } from "@/lib/utils_pharma";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, RotateCcw, ClipboardList, Search, ChevronDown, Check } from "lucide-react";

export default function PurchaseReturns() {
  const [returns, setReturns] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form states
  const [selectedPurchaseId, setSelectedPurchaseId] = useState("");
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [selectedItemIdx, setSelectedItemIdx] = useState("");
  const [returnQty, setReturnQty] = useState(1);
  const [reason, setReason] = useState("");
  const [returnDate, setReturnDate] = useState(todayISO());
  const [availableStock, setAvailableStock] = useState(null);
  const [loadingStock, setLoadingStock] = useState(false);

  // Combobox states
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [invoiceSearch, setInvoiceSearch] = useState("");
  const comboboxRef = useRef(null);

  const loadData = async () => {
    try {
      const [rRes, pRes] = await Promise.all([
        api.get("/purchase-returns"),
        api.get("/purchases"),
      ]);
      setReturns(rRes.data);
      setPurchases(pRes.data);
    } catch (err) {
      toast.error("Failed to load purchase returns data");
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Filter purchases by invoice_no or supplier_name (case-insensitive)
  const filteredPurchases = useMemo(() => {
    const q = invoiceSearch.trim().toLowerCase();
    if (!q) return purchases;
    return purchases.filter((p) => {
      const invMatch = (p.invoice_no || "").toLowerCase().includes(q);
      const supplierMatch = (p.supplier_name || "").toLowerCase().includes(q);
      return invMatch || supplierMatch;
    });
  }, [purchases, invoiceSearch]);

  // Close combobox when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (comboboxRef.current && !comboboxRef.current.contains(event.target)) {
        setInvoiceOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Update selected purchase details
  const handlePurchaseChange = (purchaseId) => {
    setSelectedPurchaseId(purchaseId);
    const purchase = purchases.find((p) => p.id === purchaseId);
    setSelectedPurchase(purchase || null);
    setSelectedItemIdx("");
    setReturnQty(1);
    setAvailableStock(null);
  };

  // Update selected item and fetch actual batch stock
  const handleItemChange = async (idxString) => {
    setSelectedItemIdx(idxString);
    setReturnQty(1);
    setAvailableStock(null);

    const idx = parseInt(idxString, 10);
    if (isNaN(idx) || !selectedPurchase || !selectedPurchase.items[idx]) return;

    const item = selectedPurchase.items[idx];
    setLoadingStock(true);

    try {
      // Query batches and medicine info for this medicine
      const { data } = await api.get(`/medicines/${item.medicine_id}/batches`);
      const batches = data.batches || [];
      const matchedBatch = batches.find((b) => b.batch_no === item.batch_no);

      if (matchedBatch) {
        setAvailableStock(matchedBatch.stock);
      } else {
        // Fallback: if no matching batch document, check the legacy medicine stock
        setAvailableStock(data.medicine?.stock || 0);
      }
    } catch (err) {
      toast.error("Failed to fetch available batch stock");
    } finally {
      setLoadingStock(false);
    }
  };

  const handleSave = async () => {
    if (!selectedPurchaseId) return toast.error("Please select a purchase invoice");
    if (selectedItemIdx === "") return toast.error("Please select a medicine batch");
    if (returnQty <= 0) return toast.error("Return quantity must be greater than 0");
    if (availableStock !== null && returnQty > availableStock) {
      return toast.error(`Return quantity cannot exceed available stock (${availableStock}) of this batch`);
    }

    const idx = parseInt(selectedItemIdx, 10);
    const item = selectedPurchase.items[idx];

    setSubmitting(true);
    try {
      const payload = {
        purchase_id: selectedPurchaseId,
        medicine_id: item.medicine_id,
        batch_no: item.batch_no || "",
        quantity: Number(returnQty),
        return_reason: reason,
        return_date: returnDate,
      };

      await api.post("/purchase-returns", payload);
      toast.success("Purchase return recorded and stock updated");
      setOpen(false);
      resetForm();
      loadData();
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setSelectedPurchaseId("");
    setSelectedPurchase(null);
    setSelectedItemIdx("");
    setReturnQty(1);
    setReason("");
    setReturnDate(todayISO());
    setAvailableStock(null);
    setInvoiceOpen(false);
    setInvoiceSearch("");
  };

  const openNew = () => {
    resetForm();
    setOpen(true);
  };

  return (
    <div className="space-y-6" data-testid="purchase-returns-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Transactions</div>
          <h1 className="text-4xl font-semibold" style={{ fontFamily: "Chivo" }}>Purchase Returns</h1>
        </div>
        <Button onClick={openNew} data-testid="add-return-btn">
          <Plus size={16} className="mr-1" /> Record Return
        </Button>
      </div>

      <div className="rounded-md border border-border bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Return Date</TableHead>
              <TableHead>Invoice Ref</TableHead>
              <TableHead>Supplier</TableHead>
              <TableHead>Medicine</TableHead>
              <TableHead>Batch</TableHead>
              <TableHead className="text-right">Qty Returned</TableHead>
              <TableHead>Reason</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {returns.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                  <ClipboardList className="mx-auto mb-2 opacity-40" size={24} />
                  No purchase returns recorded yet.
                </TableCell>
              </TableRow>
            ) : (
              returns.map((r) => (
                <TableRow key={r.id} data-testid={`return-row-${r.id}`}>
                  <TableCell className="text-sm">{formatDate(r.return_date)}</TableCell>
                  <TableCell className="text-sm font-medium">{r.purchase_invoice_no}</TableCell>
                  <TableCell className="text-sm">{r.supplier_name}</TableCell>
                  <TableCell className="text-sm font-medium">{r.medicine_name}</TableCell>
                  <TableCell className="text-sm font-mono text-xs">{r.batch_no || "—"}</TableCell>
                  <TableCell className="text-sm text-right tabular-nums font-semibold">{r.quantity}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{r.return_reason || "—"}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Record Return Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="sm:max-w-2xl max-h-[90vh] flex flex-col p-6"
          data-testid="purchase-return-dialog"
        >
          <DialogHeader className="pb-2 border-b border-border">
            <DialogTitle className="text-xl font-semibold">Record Purchase Return</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 my-2 overflow-y-auto pr-1 flex-1">
            {/* Searchable Purchase Invoice Combobox */}
            <div className="space-y-1.5" ref={comboboxRef}>
              <Label className="text-xs font-medium text-muted-foreground">Purchase Invoice *</Label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setInvoiceOpen((prev) => !prev)}
                  data-testid="select-invoice"
                  className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 transition-colors"
                >
                  {selectedPurchase ? (
                    <span className="truncate font-medium text-foreground">
                      {selectedPurchase.invoice_no}{" "}
                      <span className="text-muted-foreground font-normal">
                        ({selectedPurchase.supplier_name || "Unknown Supplier"})
                      </span>
                    </span>
                  ) : (
                    <span className="text-muted-foreground">Select Purchase Invoice</span>
                  )}
                  <ChevronDown
                    className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 ${
                      invoiceOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {invoiceOpen && (
                  <div
                    className="absolute z-50 left-0 right-0 top-full mt-1.5 bg-white border border-border rounded-lg shadow-lg overflow-hidden animate-in fade-in-0 zoom-in-95 duration-100"
                    data-testid="invoice-dropdown-menu"
                  >
                    <div className="p-2 border-b border-border bg-slate-50/70">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
                        <Input
                          placeholder="Search invoice number or supplier name..."
                          value={invoiceSearch}
                          onChange={(e) => setInvoiceSearch(e.target.value)}
                          className="pl-8 h-9 text-sm bg-white"
                          autoFocus
                          onClick={(e) => e.stopPropagation()}
                          data-testid="invoice-search-input"
                        />
                      </div>
                    </div>
                    <div className="max-h-60 overflow-y-auto divide-y divide-border/60">
                      {filteredPurchases.length === 0 ? (
                        <div
                          className="py-8 text-center text-sm text-muted-foreground"
                          data-testid="no-invoices-message"
                        >
                          No matching purchase invoices
                        </div>
                      ) : (
                        filteredPurchases.map((p) => {
                          const isSelected = p.id === selectedPurchaseId;
                          return (
                            <div
                              key={p.id}
                              onClick={() => {
                                handlePurchaseChange(p.id);
                                setInvoiceOpen(false);
                                setInvoiceSearch("");
                              }}
                              data-testid={`invoice-option-${p.id}`}
                              className={`p-3 cursor-pointer text-sm transition-colors hover:bg-muted/70 flex items-center justify-between ${
                                isSelected ? "bg-primary/5 border-l-4 border-primary" : ""
                              }`}
                            >
                              <div className="space-y-0.5">
                                <div className="font-semibold text-foreground text-sm tracking-tight">
                                  {p.invoice_no}
                                </div>
                                <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                                  <span className="font-medium text-slate-700">
                                    {p.supplier_name || "Unknown Supplier"}
                                  </span>
                                  <span>·</span>
                                  <span>{formatDate(p.invoice_date || p.purchase_date)}</span>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {p.items && p.items.length > 0 && (
                                  <span className="text-[11px] text-muted-foreground bg-slate-100 px-2 py-0.5 rounded border border-slate-200">
                                    {p.items.length} {p.items.length === 1 ? "item" : "items"}
                                  </span>
                                )}
                                {isSelected && <Check size={16} className="text-primary" />}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Selected Purchase Summary Card */}
            {selectedPurchase && (
              <div
                className="p-3.5 bg-muted/40 rounded-lg border border-border grid grid-cols-3 gap-3 text-xs"
                data-testid="selected-purchase-summary"
              >
                <div>
                  <span className="text-muted-foreground block text-[11px] uppercase tracking-wider font-semibold mb-0.5">
                    Invoice
                  </span>
                  <span className="font-semibold text-foreground font-mono text-sm">
                    {selectedPurchase.invoice_no}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground block text-[11px] uppercase tracking-wider font-semibold mb-0.5">
                    Supplier
                  </span>
                  <span
                    className="font-medium text-foreground text-xs truncate block"
                    title={selectedPurchase.supplier_name}
                  >
                    {selectedPurchase.supplier_name || "—"}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground block text-[11px] uppercase tracking-wider font-semibold mb-0.5">
                    Date
                  </span>
                  <span className="font-medium text-foreground text-xs">
                    {formatDate(selectedPurchase.invoice_date || selectedPurchase.purchase_date)}
                  </span>
                </div>
              </div>
            )}

            {/* Medicine Batch Dropdown */}
            {selectedPurchase && (
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-muted-foreground">Medicine Batch *</Label>
                <Select value={selectedItemIdx} onValueChange={handleItemChange}>
                  <SelectTrigger data-testid="select-batch" className="h-10">
                    <SelectValue placeholder="Select Medicine & Batch" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {selectedPurchase.items.map((item, idx) => (
                      <SelectItem key={idx} value={String(idx)}>
                        {item.medicine_name} (Batch: {item.batch_no || "No Batch"})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Available Stock Indicator */}
            {selectedItemIdx !== "" && (
              <div className="p-3 bg-muted/40 rounded-md border border-border flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Current Batch Stock:</span>
                <span className="font-semibold text-foreground">
                  {loadingStock ? "Fetching..." : availableStock !== null ? `${availableStock} units` : "N/A"}
                </span>
              </div>
            )}

            {/* Return Qty Input & Return Date */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-muted-foreground">Return Qty *</Label>
                <Input
                  type="number"
                  min="1"
                  max={availableStock !== null ? availableStock : undefined}
                  value={returnQty}
                  onChange={(e) => setReturnQty(Math.max(1, parseInt(e.target.value, 10) || 1))}
                  data-testid="return-qty"
                  className="h-10"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-muted-foreground">Return Date</Label>
                <Input
                  type="date"
                  value={returnDate}
                  onChange={(e) => setReturnDate(e.target.value)}
                  className="h-10"
                />
              </div>
            </div>

            {/* Return Reason Field */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-muted-foreground">Return Reason</Label>
              <Input
                placeholder="e.g. Near expiry, damaged, wrong order"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                data-testid="return-reason"
                className="h-10"
              />
            </div>
          </div>

          <DialogFooter className="pt-3 border-t border-border mt-auto">
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={submitting || loadingStock} data-testid="save-return">
              <RotateCcw size={14} className="mr-1.5" />
              {submitting ? "Saving..." : "Save Return"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

